import { useState, useEffect, useLayoutEffect } from 'react'
import { Search, X } from 'lucide-react'
import { apiFetch } from '../../api/client'
import { displayName } from '../../utils/nameHelpers'

// Scope Diagnostics Modal — extracted verbatim из Layout.jsx (2026-07-19).
// Открывается из impersonation banner кнопкой «Диагностика scope» (admin,
// пока имперсонизирует сотрудника). Показывает диагноз candidates-scope и
// quick-fix форму «Привязать к отделу» (link User -> Employee -> Department).
//
// Инцидент 2026-05-01: «IT-отдел и Разработка» -> 404 Not Found из-за
// расхождения отображаемого и точного имени отдела при ручном вводе — форма
// использует dropdown из /org/departments, не свободный текст.
//
// Родитель (Layout.jsx) владеет только самим `scopeDiag` (нужен banner'у,
// который его устанавливает); весь fix-form стейт живёт здесь и естественно
// сбрасывается при unmount (родитель размонтирует эту модалку, когда
// `scopeDiag` становится null — та же семантика, что раньше давал ручной
// сброс полей на close).
export default function ScopeDiagnosticsModal({ scopeDiag, onClose, onRefresh, user }) {
    const [fixDeptId, setFixDeptId] = useState('')
    const [fixMakeDeputy, setFixMakeDeputy] = useState(true)
    const [fixingScope, setFixingScope] = useState(false)
    const [fixResult, setFixResult] = useState(null)
    const [scopeDeptOptions, setScopeDeptOptions] = useState([]) // [{id, name}] for select
    const [scopeDeptLoading, setScopeDeptLoading] = useState(false)
    const [scopeDeptError, setScopeDeptError] = useState(null)

    useLayoutEffect(() => {
        const closeOnEscape = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return
            event.preventDefault()
            onClose()
        }
        window.addEventListener('keydown', closeOnEscape)
        return () => window.removeEventListener('keydown', closeOnEscape)
    }, [onClose])

    // ── Scope diag: загрузка списка отделов admin'a при открытии модалки ──
    // Использует original_token (под impersonate Михаил/zam не имеет доступа
    // ко всем отделам), чтобы admin видел полный список и выбирал из него,
    // а не вводил имя отдела вручную (инцидент 2026-05-01: «IT-отдел и
    // Разработка» → 404 Not Found из-за расхождения отображаемого и точного
    // имени).
    useEffect(() => {
        if (!scopeDiag?.user?.id || scopeDiag.error) return
        let cancelled = false
        async function loadDepartments() {
            setScopeDeptLoading(true)
            setScopeDeptError(null)
            try {
                // Admin impersonation: всегда использовать оригинальный admin-токен,
                // иначе под impersonated-юзером без прав /org/departments вернёт 403.
                const adminToken = localStorage.getItem('onboardix_original_token') || localStorage.getItem('onboardix_token')
                const json = await apiFetch('/org/departments', {
                    headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
                })
                if (cancelled) return
                const opts = (Array.isArray(json) ? json : [])
                    .map(d => ({ id: d.id, name: d.name }))
                    .filter(d => d.id && d.name)
                    .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
                setScopeDeptOptions(opts)
                // Pre-select linked employee's department if it's in the list,
                // чтобы admin одной кнопкой переподтвердил уже корректную привязку.
                const linkedId = scopeDiag.linked_employee?.department_id
                if (linkedId && opts.some(o => o.id === linkedId)) {
                    setFixDeptId(linkedId)
                }
            } catch (e) {
                if (!cancelled) setScopeDeptError(e?.message || String(e))
            } finally {
                if (!cancelled) setScopeDeptLoading(false)
            }
        }
        loadDepartments()
        return () => { cancelled = true }
    }, [scopeDiag?.user?.id, scopeDiag?.error, scopeDiag?.linked_employee?.department_id])

    return (
        <div
            data-app-overlay="scope-diagnostics"
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
                zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'var(--color-bg-card)',
                    color: 'var(--color-text)',
                    padding: '20px 24px',
                    borderRadius: 'var(--radius-md)',
                    maxWidth: '720px',
                    maxHeight: '80vh',
                    overflow: 'auto',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
                    fontFamily: 'monospace',
                    fontSize: '0.812rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, fontFamily: 'Inter, sans-serif' }}>
                    <strong><Search size={14} /> Candidates scope для {displayName(user?.full_name) || user?.login}</strong>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.125rem' }}>✕</button>
                </div>
                {scopeDiag.diagnosis?.length > 0 && (
                    <div style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning)', padding: 10, borderRadius: 'var(--radius-sm)', marginBottom: 12, fontFamily: 'Inter, sans-serif', fontSize: '0.812rem' }}>
                        <strong>Диагноз:</strong>
                        <ul style={{ margin: '6px 0 0 18px' }}>
                            {scopeDiag.diagnosis.map((d, i) => <li key={i}>{d}</li>)}
                        </ul>
                    </div>
                )}
                {scopeDiag.diagnosis?.length === 0 && !scopeDiag.error && (
                    <div style={{ background: 'var(--color-success-bg)', color: 'var(--color-success)', padding: 10, borderRadius: 'var(--radius-sm)', marginBottom: 12, fontFamily: 'Inter, sans-serif', fontSize: '0.812rem' }}>
                        Scope корректный — пользователь видит {scopeDiag.visible_candidates_count} из {scopeDiag.total_candidates_in_db}.
                    </div>
                )}

                {/* Quick-fix: link User → Employee → Department */}
                {scopeDiag.user?.id && !scopeDiag.error && (
                    <div style={{ background: 'var(--color-bg)', padding: 12, borderRadius: 'var(--radius-sm)', marginBottom: 12, fontFamily: 'Inter, sans-serif', fontSize: '0.812rem', border: '1px solid var(--color-border)' }}>
                        <strong>Привязать к отделу</strong>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 4, marginBottom: 8 }}>
                            Создаст или обновит карточку сотрудника + (опц.) назначит заместителем. Требует прав admin (used original_token при impersonate).
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <select
                                value={fixDeptId}
                                onChange={e => setFixDeptId(e.target.value)}
                                disabled={scopeDeptLoading || !!scopeDeptError}
                                style={{ padding: '6px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', flex: 1, minWidth: 220, background: 'var(--color-bg)' }}>
                                <option value="">
                                    {scopeDeptLoading
                                        ? 'Загрузка отделов…'
                                        : scopeDeptError
                                            ? `Не удалось загрузить (${scopeDeptError})`
                                            : `— Выберите отдел (${scopeDeptOptions.length}) —`}
                                </option>
                                {scopeDeptOptions.map(d => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                            </select>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem' }}>
                                <input type="checkbox" checked={fixMakeDeputy} onChange={e => setFixMakeDeputy(e.target.checked)} />
                                Заместитель
                            </label>
                            <button
                                onClick={async () => {
                                    if (!fixDeptId) return
                                    setFixingScope(true)
                                    setFixResult(null)
                                    try {
                                        // Admin impersonation: всегда оригинальный admin-токен.
                                        const adminToken = localStorage.getItem('onboardix_original_token') || localStorage.getItem('onboardix_token')
                                        const json = await apiFetch(`/auth/admin/users/${scopeDiag.user.id}/link-employee-to-department`, {
                                            method: 'POST',
                                            headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
                                            body: JSON.stringify({ department_id: fixDeptId, make_deputy: fixMakeDeputy }),
                                        })
                                        setFixResult({ ok: true, data: json })
                                        // Auto-refresh scope diagnostics после исправления.
                                        try {
                                            const refreshed = await apiFetch('/auth/me/candidates-scope')
                                            onRefresh(refreshed)
                                        } catch { /* fall through, fixResult покажет статус */ }
                                    } catch (e) {
                                        setFixResult({ ok: false, error: e?.message || String(e) })
                                    } finally {
                                        setFixingScope(false)
                                    }
                                }}
                                disabled={fixingScope || !fixDeptId}
                                style={{
                                    background: fixingScope || !fixDeptId ? 'var(--color-text-muted)' : 'var(--color-success)',
                                    color: 'var(--color-text-inverse)',
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: fixingScope || !fixDeptId ? 'not-allowed' : 'pointer',
                                    fontWeight: 600,
                                }}>
                                {fixingScope ? 'Привязываем…' : 'Привязать'}
                            </button>
                        </div>
                        {fixResult?.ok && (
                            <div style={{ marginTop: 8, padding: 8, background: 'var(--color-success-bg)', color: 'var(--color-success)', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem' }}>
                                Готово. Действия:
                                <ul style={{ margin: '4px 0 0 18px' }}>
                                    {(fixResult.data?.actions || []).map((a, i) => <li key={i}>{a}</li>)}
                                </ul>
                            </div>
                        )}
                        {fixResult?.ok === false && (
                            <div style={{ marginTop: 8, padding: 8, background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem' }}>
                                Ошибка: {fixResult.error}
                            </div>
                        )}
                    </div>
                )}

                {JSON.stringify(scopeDiag, null, 2)}
            </div>
        </div>
    )
}
